import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { Subscription } from 'rxjs';
import { AdvancedModelSettingsService } from '../../core/services/advanced-model-settings.service';
import { LiveTranslationSettingsService } from '../../core/services/live-translation-settings.service';
import { AuthService } from '../../core/services/auth.service';
import {
  TranslationKeys,
  TranslationService,
} from '../../core/services/translation.service';
import { VersionService } from '../../core/services/version.service';
import { LanguageOption, User, Voice } from '../../shared/models';

@Component({
  selector: 'app-navigation',
  templateUrl: './navigation.component.html',
  styleUrls: ['./navigation.component.css'],
  standalone: true,
  imports: [CommonModule, FormsModule],
})
export class NavigationComponent implements OnInit, OnDestroy, OnChanges {
  private authService = inject(AuthService);
  private versionService = inject(VersionService);
  private router = inject(Router);

  @Input() selectedVoice: string = 'alloy';
  @Input() availableVoices: Voice[] = [];
  @Input() currentUser: User | null = null;
  @Input() isLoggedIn: boolean = false;
  @Input() selectedUserLanguage:
    | 'en'
    | 'ko'
    | 'zh-CN'
    | 'zh-HK'
    | 'ky'
    | 'ru'
    | 'kk'
    | 'tg'
    | 'tk'
    | 'uz'
    | 'fa'
    | 'es' = 'en';
  @Input() selectedPartnerLanguage:
    | 'en'
    | 'ko'
    | 'zh-CN'
    | 'zh-HK'
    | 'ky'
    | 'ru'
    | 'kk'
    | 'tg'
    | 'tk'
    | 'uz'
    | 'fa'
    | 'es' = 'ky';
  @Input() speakingSpeed: number = 1.0;
  @Input() useDiarization: boolean = false;
  @Output() voiceSelected = new EventEmitter<string>();
  @Output() logout = new EventEmitter<void>();
  @Output() showHistory = new EventEmitter<void>();
  @Output() showWordUsage = new EventEmitter<void>();
  @Output() showImageUsage = new EventEmitter<void>();
  @Output() showAnalytics = new EventEmitter<void>();
  @Output() showBuyWords = new EventEmitter<void>();
  @Output() showLanguageSettings = new EventEmitter<void>();
  @Output() languageChanged = new EventEmitter<{
    userLanguage: string;
    partnerLanguage: string;
  }>();
  @Output() speedChanged = new EventEmitter<number>();
  @Output() diarizationToggled = new EventEmitter<boolean>();
  @Output() menuStateChanged = new EventEmitter<boolean>();

  isMobileMenuOpen: boolean = false;
  isLanguageMenuOpen = false;

  // Analytics permission cache
  canViewAnalyticsCache: boolean = false;
  private isUpdatingAnalyticsPermission = false; // Flag to prevent duplicate calls
  private analyticsPermissionTimeout?: number; // Debounce timer

  // Translation properties
  translations: TranslationKeys = {} as TranslationKeys;
  private translationSubscription?: Subscription;
  private advancedModelSubscription?: Subscription;
  globalAdvancedModelEnabled = false;
  private liveTranslationSubscription?: Subscription;
  liveTranslationEnabled = false;

  // Dynamic logo URL based on language
  logoUrl: string =
    'https://firebasestorage.googleapis.com/v0/b/translation-5c1b8.firebasestorage.app/o/app_log.jpeg?alt=media&token=d650fe3a-54da-4ca7-ab7f-f5ea1e23e344';

  constructor(
    private advancedModelSettings: AdvancedModelSettingsService,
    private liveTranslationSettings: LiveTranslationSettingsService,
    private translationService: TranslationService,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone
  ) {}

  // Complete language options for both dropdowns
  allLanguages: LanguageOption[] = [
    { value: 'en', label: 'English' },
    { value: 'ko', label: 'Korean (한국어)' },
    { value: 'zh-CN', label: 'Mandarin (普通话)' },
    { value: 'zh-HK', label: 'Cantonese (粵語)' },
    { value: 'ky', label: 'Kyrgyz (Кыргызча)' },
    { value: 'ru', label: 'Russian (Русский)' },
    { value: 'kk', label: 'Kazakh (Қазақша)' },
    { value: 'tg', label: 'Tajik (Тоҷикӣ)' },
    { value: 'tk', label: 'Turkmen (Türkmençe)' },
    { value: 'uz', label: "Uzbek (O'zbekcha)" },
    { value: 'fa', label: 'Farsi (فارسی)' },
    { value: 'es', label: 'Spanish (Español)' },
  ];

  // Filtered language options (excludes currently selected language from other dropdown)
  get userLanguages(): LanguageOption[] {
    return this.allLanguages.filter(
      (lang) => lang.value !== this.selectedPartnerLanguage
    );
  }

  get partnerLanguages(): LanguageOption[] {
    return this.allLanguages.filter(
      (lang) => lang.value !== this.selectedUserLanguage
    );
  }

  // Version management
  currentVersion: number = 2.0; // Updated to 2.0.0
  isNewVersion: boolean = false;

  selectVoice(voiceId: string) {
    this.selectedVoice = voiceId;
    this.voiceSelected.emit(voiceId);
  }

  toggleMobileMenu(event?: Event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    console.log(
      '🍔 Menu button clicked! Before toggle - isMobileMenuOpen:',
      this.isMobileMenuOpen
    );
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
    console.log('🍔 After toggle - isMobileMenuOpen:', this.isMobileMenuOpen);
    this.menuStateChanged.emit(this.isMobileMenuOpen);
    this.cdr.markForCheck();
    this.cdr.detectChanges();
  }

  closeMobileMenu() {
    this.isMobileMenuOpen = false;
    this.menuStateChanged.emit(this.isMobileMenuOpen);
  }

  // Debug method to check current state
  getMenuState() {
    console.log('Current menu state:', this.isMobileMenuOpen);
    return this.isMobileMenuOpen;
  }

  onLogout() {
    this.logout.emit();
  }

  onShowHistory() {
    console.log('Navigation onShowHistory called, emitting showHistory event');
    this.showHistory.emit();
  }

  onShowWordUsage() {
    console.log(
      'Navigation onShowWordUsage called, emitting showWordUsage event'
    );
    this.showWordUsage.emit();
  }

  onShowAnalytics() {
    console.log(
      'Navigation onShowAnalytics called, emitting showAnalytics event'
    );
    this.showAnalytics.emit();
  }

  onShowLanguageSettings() {
    console.log(
      '🔧 Navigation onShowLanguageSettings called, emitting showLanguageSettings event'
    );
    console.log('🔧 showLanguageSettings emitter:', this.showLanguageSettings);
    this.showLanguageSettings.emit();
    console.log('🔧 Event emitted successfully');
    // Close mobile menu after emitting the event
    this.closeMobileMenu();
  }

  onUserLanguageChange() {
    console.log('User language changed to:', this.selectedUserLanguage);
    this.languageChanged.emit({
      userLanguage: this.selectedUserLanguage,
      partnerLanguage: this.selectedPartnerLanguage,
    });
  }

  onPartnerLanguageChange() {
    console.log('Partner language changed to:', this.selectedPartnerLanguage);
    this.languageChanged.emit({
      userLanguage: this.selectedUserLanguage,
      partnerLanguage: this.selectedPartnerLanguage,
    });
  }

  getFilteredPartnerLanguages(): LanguageOption[] {
    return this.partnerLanguages;
  }

  onShowBuyWords() {
    console.log(
      'Navigation onShowBuyWords called, emitting showBuyWords event'
    );
    this.showBuyWords.emit();
  }

  // Get button label based on platform - mobile shows "Buy Data From Webapp", web shows normal translation
  getBuyWordsButtonLabel(): string {
    if (Capacitor.isNativePlatform()) {
      return this.translations.buyDataFromWebapp;
    }
    return this.translations.buyWords || 'Buy Translation Data';
  }

  // Check if platform is iOS
  isIOS(): boolean {
    return Capacitor.getPlatform() === 'ios';
  }

  onShowImageUsage() {
    console.log(
      'Navigation onShowImageUsage called, emitting showImageUsage event'
    );
    this.showImageUsage.emit();
  }

  toggleLanguageMenu() {
    this.isLanguageMenuOpen = !this.isLanguageMenuOpen;
  }

  closeLanguageMenu() {
    this.isLanguageMenuOpen = false;
  }

  onSpeedChange() {
    this.speedChanged.emit(this.speakingSpeed);
  }

  onGlobalAdvancedModelChange(enabled: boolean) {
    this.advancedModelSettings.setGlobalAdvancedModelEnabled(enabled);
    console.log('🧠 Global advanced translation model toggled:', enabled);
  }

  onLiveTranslationChange(enabled: boolean) {
    this.liveTranslationSettings.setLiveTranslationEnabled(enabled);
  }

  showLiveTranslationInfo(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    window.alert(
      this.translations.liveTranslationDescription ||
        'Translates while you speak in Continuous Conversation and Broadcast.'
    );
  }

  showGlobalAdvancedModelInfo(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    window.alert(
      this.translations.advancedModelDescription ||
        'Uses gpt-5.4-mini. May be slower.'
    );
  }

  toggleDiarization() {
    this.useDiarization = !this.useDiarization;
    this.diarizationToggled.emit(this.useDiarization);
    console.log('👥 Diarization toggled:', this.useDiarization);
  }

  getUserLanguageName(lang: string): string {
    const option = this.userLanguages.find((l) => l.value === lang);
    return option ? option.label : lang;
  }

  getPartnerLanguageName(lang: string): string {
    const option = this.partnerLanguages.find((l) => l.value === lang);
    return option ? option.label : lang;
  }

  ngOnInit() {
    this.checkVersion();
    // Setup subscription first to catch language changes
    this.setupTranslationSubscription();
    // Load translations after subscription is set up
    this.loadTranslations();
    this.advancedModelSubscription =
      this.advancedModelSettings.globalAdvancedModel$.subscribe((enabled) => {
        this.globalAdvancedModelEnabled = enabled;
        this.cdr.markForCheck();
      });
    this.liveTranslationSubscription =
      this.liveTranslationSettings.liveTranslation$.subscribe((enabled) => {
        this.liveTranslationEnabled = enabled;
        this.cdr.markForCheck();
      });
    // Force change detection to ensure UI updates
    this.cdr.detectChanges();

    // Analytics permission will be updated by ngOnChanges when inputs are set
    // No need to call updateAnalyticsPermission here to avoid duplicate calls

    // Debug logging for analytics menu visibility
    console.log('🔧 NavigationComponent initialized');
    console.log('🔧 Initial state:', {
      isLoggedIn: this.isLoggedIn,
      currentUser: this.currentUser,
      canViewAnalyticsCache: this.canViewAnalyticsCache,
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    // Update analytics permission cache when inputs change
    // Only call once per change detection cycle, even if both currentUser and isLoggedIn change
    if (changes['currentUser'] || changes['isLoggedIn']) {
      // Clear any existing timeout to prevent multiple calls
      if (this.analyticsPermissionTimeout) {
        clearTimeout(this.analyticsPermissionTimeout);
      }

      // Use debounced timeout to ensure this runs only once after all changes
      this.analyticsPermissionTimeout = window.setTimeout(() => {
        this.updateAnalyticsPermission();
        this.analyticsPermissionTimeout = undefined;
      }, 10); // Small delay to debounce multiple rapid changes

      // Log when inputs change, especially currentUser
      if (changes['currentUser']) {
        console.log('🔧 currentUser input changed:', {
          previous: changes['currentUser'].previousValue,
          current: changes['currentUser'].currentValue,
          currentUserEmail: this.currentUser?.email,
          currentUserRole: this.currentUser?.role,
          currentUserRoles: this.currentUser?.roles,
          currentUserPermissions: this.currentUser?.permissions,
          isLoggedIn: this.isLoggedIn,
          canViewAnalyticsCache: this.canViewAnalyticsCache,
        });
      }

      if (changes['isLoggedIn']) {
        console.log('🔧 isLoggedIn input changed:', {
          previous: changes['isLoggedIn'].previousValue,
          current: changes['isLoggedIn'].currentValue,
          canViewAnalyticsCache: this.canViewAnalyticsCache,
        });
      }
    }
  }

  ngOnDestroy() {
    if (this.translationSubscription) {
      this.translationSubscription.unsubscribe();
    }
    this.advancedModelSubscription?.unsubscribe();
    this.liveTranslationSubscription?.unsubscribe();

    // Clean up analytics permission timeout
    if (this.analyticsPermissionTimeout) {
      clearTimeout(this.analyticsPermissionTimeout);
      this.analyticsPermissionTimeout = undefined;
    }
  }

  private loadTranslations() {
    const currentLang = this.translationService.getCurrentLanguage();
    console.log(
      '🔄 NavigationComponent: Loading translations for language:',
      currentLang
    );
    // Create a new object reference to ensure Angular detects the change
    const newTranslations = this.translationService.getAllTranslations();
    // Use Object.assign to create a completely new object
    this.translations = Object.assign({}, newTranslations);
    console.log('🔄 NavigationComponent: Loaded translations:', {
      translation: this.translations.translation,
      wordUsage: this.translations.wordUsage,
      imageUsage: this.translations.imageUsage,
      analytics: this.translations.analytics,
      languageSettings: this.translations.languageSettings,
      history: this.translations.history,
      currentLang: currentLang,
    });
    // Verify translations are actually Russian if language is Russian
    if (currentLang === 'ru') {
      console.log('🔄 NavigationComponent: Verifying Russian translations:', {
        translationIsRussian: this.translations.translation === 'Переводчик',
        wordUsageIsRussian:
          this.translations.wordUsage === 'Использование слов',
        imageUsageIsRussian:
          this.translations.imageUsage === 'Использование изображений',
      });
    }
  }

  /**
   * Update logo URL based on current language setting
   * Maps language codes to appropriate logo images
   */
  private updateLogoUrl(language: string): void {
    // Map of language codes to logo URLs
    // Default to app_log.jpeg for most languages
    const logoMap: { [key: string]: string } = {
      en: 'https://firebasestorage.googleapis.com/v0/b/translation-5c1b8.firebasestorage.app/o/app_log.jpeg?alt=media&token=d650fe3a-54da-4ca7-ab7f-f5ea1e23e344',
      ko: 'https://firebasestorage.googleapis.com/v0/b/translation-5c1b8.firebasestorage.app/o/app_log.jpeg?alt=media&token=d650fe3a-54da-4ca7-ab7f-f5ea1e23e344',
      'zh-CN':
        'https://firebasestorage.googleapis.com/v0/b/translation-5c1b8.firebasestorage.app/o/app_log.jpeg?alt=media&token=d650fe3a-54da-4ca7-ab7f-f5ea1e23e344',
      'zh-HK':
        'https://firebasestorage.googleapis.com/v0/b/translation-5c1b8.firebasestorage.app/o/app_log.jpeg?alt=media&token=d650fe3a-54da-4ca7-ab7f-f5ea1e23e344',
      zh: 'https://firebasestorage.googleapis.com/v0/b/translation-5c1b8.firebasestorage.app/o/app_log.jpeg?alt=media&token=d650fe3a-54da-4ca7-ab7f-f5ea1e23e344',
      ky: 'https://firebasestorage.googleapis.com/v0/b/translation-5c1b8.firebasestorage.app/o/app_log.jpeg?alt=media&token=d650fe3a-54da-4ca7-ab7f-f5ea1e23e344',
      ru: 'https://firebasestorage.googleapis.com/v0/b/translation-5c1b8.firebasestorage.app/o/app_log.jpeg?alt=media&token=d650fe3a-54da-4ca7-ab7f-f5ea1e23e344',
      kk: 'https://firebasestorage.googleapis.com/v0/b/translation-5c1b8.firebasestorage.app/o/app_log.jpeg?alt=media&token=d650fe3a-54da-4ca7-ab7f-f5ea1e23e344',
      tg: 'https://firebasestorage.googleapis.com/v0/b/translation-5c1b8.firebasestorage.app/o/app_log.jpeg?alt=media&token=d650fe3a-54da-4ca7-ab7f-f5ea1e23e344',
      tk: 'https://firebasestorage.googleapis.com/v0/b/translation-5c1b8.firebasestorage.app/o/app_log.jpeg?alt=media&token=d650fe3a-54da-4ca7-ab7f-f5ea1e23e344',
      uz: 'https://firebasestorage.googleapis.com/v0/b/translation-5c1b8.firebasestorage.app/o/app_log.jpeg?alt=media&token=d650fe3a-54da-4ca7-ab7f-f5ea1e23e344',
      fa: 'https://firebasestorage.googleapis.com/v0/b/translation-5c1b8.firebasestorage.app/o/app_log.jpeg?alt=media&token=d650fe3a-54da-4ca7-ab7f-f5ea1e23e344',
      es: 'https://firebasestorage.googleapis.com/v0/b/translation-5c1b8.firebasestorage.app/o/app_log.jpeg?alt=media&token=d650fe3a-54da-4ca7-ab7f-f5ea1e23e344',
    };

    // Get logo URL for current language, fallback to default
    this.logoUrl = logoMap[language] || logoMap['en'];
  }

  private setupTranslationSubscription() {
    // Initialize logo with current language
    this.updateLogoUrl(this.translationService.getCurrentLanguage());
    // Subscribe to language changes - BehaviorSubject will emit current value immediately
    this.translationSubscription =
      this.translationService.currentLanguage$.subscribe((language) => {
        console.log(
          '🔄 NavigationComponent: Language subscription triggered, language:',
          language
        );
        // Run inside Angular zone to ensure change detection
        this.ngZone.run(() => {
          const currentLang =
            language || this.translationService.getCurrentLanguage();
          console.log(
            '🔄 NavigationComponent: Processing language change to:',
            currentLang
          );
          // Load translations immediately
          this.loadTranslations();
          // Update logo when language changes
          this.updateLogoUrl(currentLang);
          // Force change detection to update the UI immediately
          setTimeout(() => {
            this.cdr.detectChanges();
            this.cdr.markForCheck();
          }, 0);
        });
      });
  }

  private checkVersion() {
    try {
      // Get version from service (dynamically loaded from package.json/assets)
      const versionString = this.versionService.getVersion();
      const currentVersion = parseFloat(versionString);

      // Initialize currentVersion if not set
      if (this.currentVersion === 0) {
        this.currentVersion = currentVersion;
      }

      const storedVersion = localStorage.getItem('appVersion');
      const storedVersionNumber = storedVersion ? parseFloat(storedVersion) : 0;

      console.log(
        `🔍 Version check: stored=${storedVersionNumber}, current=${currentVersion}, versionString=${versionString}`
      );

      if (storedVersionNumber < currentVersion) {
        // New version detected
        this.isNewVersion = true;
        console.log(
          `🆕 New version detected! Upgrading from v${storedVersionNumber} to v${currentVersion}`
        );

        // Update stored version
        localStorage.setItem('appVersion', currentVersion.toString());
        this.currentVersion = currentVersion;

        // Show version upgrade notification
        this.showVersionUpgradeNotification(
          storedVersionNumber,
          currentVersion
        );
      } else if (storedVersionNumber === currentVersion) {
        // Same version
        this.currentVersion = currentVersion;
        this.isNewVersion = false;
        console.log(`✅ App version: v${this.currentVersion} (up to date)`);
      } else {
        // Stored version is newer (shouldn't happen in normal cases)
        this.currentVersion = storedVersionNumber;
        this.isNewVersion = false;
        console.log(
          `⚠️ Stored version (v${storedVersionNumber}) is newer than current (v${currentVersion})`
        );
      }
    } catch (error) {
      console.error('Error checking version:', error);
      // Fallback: set current version from service
      try {
        this.currentVersion = parseFloat(this.versionService.getVersion());
        localStorage.setItem('appVersion', this.currentVersion.toString());
        this.isNewVersion = false;
        console.log(`🔄 Fallback: Set version to ${this.currentVersion}`);
      } catch (fallbackError) {
        console.error('Fallback version setting failed:', fallbackError);
        this.currentVersion = 1.0; // Ultimate fallback
        this.isNewVersion = false;
      }
    }
  }

  private showVersionUpgradeNotification(
    oldVersion: number,
    newVersion: number
  ) {
    // Show a notification about the version upgrade
    const message = `App updated to version ${newVersion}!${
      oldVersion > 0 ? ` (was v${oldVersion})` : ''
    }`;

    // You can customize this notification method
    // For now, we'll use console and could add a toast notification later
    console.log(`🎉 ${message}`);

    // Optional: Show browser notification if supported
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification('App Updated!', {
        body: message,
        icon: '/favicon.ico',
      });
    }
  }

  getVersionDisplay(): string {
    // Always get the latest version from the service
    return this.versionService.getVersionDisplay();
  }

  getVersionStatus(): string {
    return this.isNewVersion ? 'New!' : 'Current';
  }

  /**
   * Manually update the app version in localStorage
   * Useful for testing or forcing a version update
   */
  updateStoredVersion(newVersion: number): void {
    try {
      const oldVersion = localStorage.getItem('appVersion');
      localStorage.setItem('appVersion', newVersion.toString());
      this.currentVersion = newVersion;
      this.isNewVersion =
        newVersion > parseFloat(this.versionService.getVersion());

      console.log(
        `🔄 Manually updated version from ${oldVersion} to ${newVersion}`
      );
    } catch (error) {
      console.error('Error updating stored version:', error);
    }
  }

  /**
   * Clear the stored version (useful for testing)
   */
  clearStoredVersion(): void {
    try {
      localStorage.removeItem('appVersion');
      console.log('🗑️ Cleared stored version from localStorage');

      // Re-check version to trigger upgrade flow
      this.checkVersion();
    } catch (error) {
      console.error('Error clearing stored version:', error);
    }
  }

  /**
   * Get the stored version from localStorage
   */
  getStoredVersion(): number {
    try {
      const storedVersion = localStorage.getItem('appVersion');
      return storedVersion ? parseInt(storedVersion, 10) : 0;
    } catch (error) {
      console.error('Error getting stored version:', error);
      return 0;
    }
  }

  /**
   * Check if current user has admin role
   */
  isAdmin(): boolean {
    return this.authService.isAdmin();
  }

  /**
   * Check if current user has a specific permission
   */
  hasPermission(permission: string): boolean {
    return this.authService.hasPermission(permission);
  }

  /**
   * Update the analytics permission cache
   * This is called once when inputs change, not on every change detection cycle
   */
  private updateAnalyticsPermission(): void {
    // Prevent duplicate calls
    if (this.isUpdatingAnalyticsPermission) {
      console.log(
        '🔍 updateAnalyticsPermission already in progress, skipping duplicate call'
      );
      return;
    }

    this.isUpdatingAnalyticsPermission = true;

    try {
      // Check permissions from the @Input currentUser directly (more reliable on mobile)
      const hasPermissionFromInput =
        this.currentUser?.permissions?.includes('read:analytics') || false;
      const hasPermissionFromService = this.hasPermission('read:analytics');
      const newCacheValue = hasPermissionFromInput || hasPermissionFromService;

      // Only update if the value has changed to avoid unnecessary change detection
      if (this.canViewAnalyticsCache !== newCacheValue) {
        this.canViewAnalyticsCache = newCacheValue;
      }

      console.log(
        '🔍 updateAnalyticsPermission - canViewAnalyticsCache:',
        this.canViewAnalyticsCache
      );
      console.log(
        '🔍 updateAnalyticsPermission - hasPermissionFromInput:',
        hasPermissionFromInput
      );
      console.log(
        '🔍 updateAnalyticsPermission - hasPermissionFromService:',
        hasPermissionFromService
      );
    } finally {
      this.isUpdatingAnalyticsPermission = false;
    }
  }

  /**
   * Check if current user has analytics permission (read from cache)
   */
  canViewAnalytics(): boolean {
    return this.canViewAnalyticsCache;
  }

  /**
   * Manually refresh analytics permission (useful for debugging)
   */
  refreshAnalyticsPermission(): void {
    console.log('🔄 Manually refreshing analytics permission...');
    this.updateAnalyticsPermission();
  }

  /**
   * Open support page - navigate to /support route
   */
  async openSupport(): Promise<void> {
    // For web app, use Angular Router
    if (!Capacitor.isNativePlatform()) {
      this.router.navigate(['/support']);
      return;
    }
    
    // For mobile app, navigate within the app
    try {
      this.router.navigate(['/support']);
    } catch (error) {
      console.error('Error navigating to support page:', error);
      // Fallback: open in external browser
      const supportUrl = window.location.origin + '/support';
      await Browser.open({ url: supportUrl });
    }
  }
}
